// Lazy load Firebase imports to reduce initial bundle size
// Firebase will be loaded dynamically after UI is mounted
let firebase = null;
let firebaseLoaded = false;

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBkLEz6ADgOx78QEJDrJrd1Lnepuwqii0g",
  authDomain: "varaha-silks.firebaseapp.com",
  projectId: "varaha-silks",
  storageBucket: "varaha-silks.firebasestorage.app",
  messagingSenderId: "812217696139",
  appId: "1:812217696139:web:83372cb58b290576de798e",
  databaseURL: "https://varaha-silks-default-rtdb.asia-southeast1.firebasedatabase.app"
};

// Initialize Firebase with error handling and non-blocking approach
let app = null;
let db = null;
let auth = null;
let storage = null;
let realtimeDb = null;
let initializationPromise = null;
let isInitializing = false;
let initializationTimeout = null;

// Detect mobile device for longer timeouts
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768);
};

// Lazy load Firebase SDK
const loadFirebase = async () => {
  if (firebaseLoaded && firebase) {
    return firebase;
  }
  
  try {
    // Dynamically import Firebase modules
    // These imports are needed to register the modules with Firebase compat API
    const [firebaseApp] = await Promise.all([
      import("firebase/compat/app"),
      import("firebase/compat/firestore"), // Required for Firestore
      import("firebase/compat/auth"), // Required for Auth
      import("firebase/compat/storage"), // Required for Storage
      import("firebase/compat/database") // Required for Realtime Database
    ]);
    
    firebase = firebaseApp.default;
    firebaseLoaded = true;
    return firebase;
  } catch (error) {
    console.error('Failed to load Firebase SDK:', error);
    throw error;
  }
};

// Non-blocking Firebase initialization with timeout
// This will be called after UI is mounted to prevent blocking first paint
const initializeFirebase = async () => {
  // Return existing promise if already initializing
  if (initializationPromise) {
    return initializationPromise;
  }

  // Return immediately if already initialized
  if (app && db && auth) {
    return Promise.resolve({ app, db, auth, storage, realtimeDb });
  }

  // Prevent multiple simultaneous initializations
  if (isInitializing) {
    const waitTime = isMobileDevice() ? 15000 : 10000;
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!isInitializing && app && db && auth) {
          clearInterval(checkInterval);
          resolve({ app, db, auth, storage, realtimeDb });
        }
      }, 100);
      
      // Wait longer for initialization to complete
      setTimeout(() => {
        clearInterval(checkInterval);
        // Only resolve if we have at least the core services
        if (app && db && auth) {
          resolve({ app, db, auth, storage, realtimeDb });
        } else {
          // If still not ready, wait a bit more and retry
          setTimeout(() => {
            resolve({ app, db, auth, storage, realtimeDb });
          }, 2000);
        }
      }, waitTime);
    });
  }

  isInitializing = true;

  // Use much longer timeout to allow Firebase SDK to load properly
  // Firebase SDK loading can take 2-5 seconds on slower networks
  const initTimeout = isMobileDevice() ? 15000 : 10000;

  initializationPromise = new Promise(async (resolve) => {
    // Set timeout - much longer to allow Firebase SDK to load
    // Only warn if we don't have core services after timeout
    initializationTimeout = setTimeout(() => {
      if (!app || !db || !auth) {
        console.warn('⚠️ Firebase initialization timeout - some services may not be available');
        // Don't resolve with null services - wait a bit more
        setTimeout(() => {
          isInitializing = false;
          resolve({ app, db, auth, storage, realtimeDb });
        }, 2000);
      } else {
        isInitializing = false;
        resolve({ app, db, auth, storage, realtimeDb });
      }
    }, initTimeout);

    try {
      // Lazy load Firebase SDK first
      const firebaseModule = await loadFirebase();
      if (!firebaseModule) {
        throw new Error('Failed to load Firebase SDK');
      }
      
      // Initialize Firebase app (synchronous, fast)
      if (!firebaseModule.apps || firebaseModule.apps.length === 0) {
        app = firebaseModule.initializeApp(firebaseConfig);
      } else {
        app = firebaseModule.app();
      }

      // Initialize services (synchronous, fast)
      db = firebaseModule.firestore();
      auth = firebaseModule.auth();
      storage = firebaseModule.storage();
      realtimeDb = firebaseModule.database();

      // Enable persistence in background (non-blocking)
      // Note: Using compat API, enablePersistence is deprecated but still functional
      // The new FirestoreSettings.cache approach requires v9+ modular SDK
      // We'll keep using enablePersistence for now as it works with compat API
      // This warning can be ignored until we migrate to v9+ modular SDK
      if (db && typeof db.enablePersistence === 'function') {
        // Suppress the deprecation warning by catching and handling it
        db.enablePersistence({
          synchronizeTabs: false // Disable multi-tab sync to reduce warnings
        })
          .catch((persistenceError) => {
            // Persistence may fail in some browsers (e.g., private browsing)
            // Silently handle expected errors to reduce console noise
            if (persistenceError.code === 'failed-precondition') {
              // User may have other tabs open - this is expected
            } else if (persistenceError.code === 'unimplemented') {
              // Not supported in this browser - expected
            }
            // Only log unexpected errors
          });
      }

      // Clear timeout and resolve
      if (initializationTimeout) {
        clearTimeout(initializationTimeout);
        initializationTimeout = null;
      }
      
      isInitializing = false;
      resolve({ app, db, auth, storage, realtimeDb });
    } catch (error) {
      console.error('Firebase initialization error:', error);
      // Clear timeout
      if (initializationTimeout) {
        clearTimeout(initializationTimeout);
        initializationTimeout = null;
      }
      
      isInitializing = false;
      // Resolve with available services (may be null)
      resolve({ app, db, auth, storage, realtimeDb });
    }
  });

  return initializationPromise;
};

// Initialize Firebase as soon as possible (non-blocking)
// Start immediately to reduce wait time for components
if (typeof window !== 'undefined') {
  // Start initialization immediately but don't block
  // Use requestIdleCallback with shorter timeout to start earlier
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => {
      initializeFirebase().catch((error) => {
        console.error('Firebase initialization failed:', error);
        isInitializing = false;
      });
    }, { timeout: 500 }); // Reduced from 2000ms to start earlier
  } else {
    // Fallback: start immediately with minimal delay
    setTimeout(() => {
      initializeFirebase().catch((error) => {
        console.error('Firebase initialization failed:', error);
        isInitializing = false;
      });
    }, 50); // Reduced from 100ms to start earlier
  }
}

// Export Firebase services with null checks and error handling
export { db, auth, storage, realtimeDb };
export default app;

// Add a function to check if Firebase is properly initialized
export const isFirebaseInitialized = () => {
  return !!(app && db && auth);
};

// Get Firebase services (non-blocking, returns immediately)
export const getFirebaseServicesSync = () => {
  return { app, db, auth, storage, realtimeDb };
};

// Ensure Firebase is initialized (waits for initialization to complete)
export const ensureFirebaseInitialized = async () => {
  if (app && db && auth) {
    return { app, db, auth, storage, realtimeDb };
  }
  
  // If already initializing, wait for it
  if (isInitializing && initializationPromise) {
    return initializationPromise;
  }
  
  // Start initialization and wait for it
  const result = await initializeFirebase();
  
  // If services are still null after initialization, wait a bit more
  if (!result.db || !result.auth) {
    // Wait up to 3 more seconds for services to be ready
    const maxWait = 3000;
    const startTime = Date.now();
    while ((!app || !db || !auth) && (Date.now() - startTime < maxWait)) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return { app, db, auth, storage, realtimeDb };
  }
  
  return result;
};

// Ensure Firestore is ready before use
export const ensureFirestoreReady = async () => {
  await ensureFirebaseInitialized();
  if (!db) {
    throw new Error('Firestore database not initialized. Please refresh the page.');
  }
  return db;
};

// Add a function to safely get Firebase services
export const getFirebaseServices = () => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase not properly initialized');
    return { db: null, auth: null, storage: null, realtimeDb: null };
  }
  return { db, auth, storage, realtimeDb };
};

// Network connectivity helpers
export const enableFirestoreNetwork = async () => {
  try {
    if (db) {
      await db.enableNetwork();
      console.log('✅ Firestore network enabled');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error enabling Firestore network:', error);
    return false;
  }
};

export const disableFirestoreNetwork = async () => {
  try {
    if (db) {
      await db.disableNetwork();
      console.log('⚠️ Firestore network disabled');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error disabling Firestore network:', error);
    return false;
  }
};

export const checkFirestoreNetworkStatus = async () => {
  try {
    if (!db) return { online: false, error: 'Firestore not initialized' };
    
    // Try a simple read operation to check connectivity
    const testRef = db.collection('_test').doc('_connection');
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection test timeout')), 5000);
    });
    
    try {
      await Promise.race([testRef.get(), timeoutPromise]);
      return { online: true };
    } catch (testError) {
      // If it's a permission error, network is working but we don't have access
      // If it's a timeout, network might be down
      if (testError.code === 'permission-denied') {
        return { online: true, note: 'Network working but test collection not accessible' };
      }
      return { online: false, error: testError.message };
    }
  } catch (error) {
    return { online: false, error: error.message };
  }
};
